<script lang="ts">
  /**
   * Adapted from:
   * https://raw.githubusercontent.com/paulcollett/react-masonry-css/master/src/react-masonry-css.js
   */

  import SolidReviewCard from 'src/components/solid-review-card.svelte';
  import type { BusinessReviewEntry } from 'src/function-utils/data-models';
  import { BusinessReviewEntryType } from 'src/function-utils/data-models';
  import { afterUpdate, onDestroy, onMount } from 'svelte';
  import { flip } from 'svelte/animate';
  import { quintOut } from 'svelte/easing';
  import { crossfade } from 'svelte/transition';

  export let businessKey: string;
  export let reviews: BusinessReviewEntry[];
  export let isCelebrity: boolean = false;
  export let breakpointCols:
    | number
    | { default?: number; [key: number]: number } = undefined;
  export let columnClassName: string = '';

  const DEFAULT_COLUMNS = 1;

  const [send, receive] = crossfade({
    duration: (d) => Math.sqrt(d * 200),

    fallback(node, _params) {
      const style = getComputedStyle(node);
      const transform = style.transform === 'none' ? '' : style.transform;

      return {
        duration: 600,
        easing: quintOut,
        css: (t) => `
					transform: ${transform} translateY(${(1 - t) * 20}px);
					opacity: ${t}
				`,
      };
    },
  });

  let _lastRecalculateAnimationFrame: number;
  let masonryContainer: HTMLDivElement;
  let resizeObserver: ResizeObserver;

  // default state
  let _columnCount = DEFAULT_COLUMNS;

  onMount(() => {
    reCalculateColumnCount();

    if (ResizeObserver) {
      resizeObserver = new ResizeObserver(reCalculateColumnCountDebounce);
      resizeObserver.observe(masonryContainer);
    }
  });

  afterUpdate(() => {
    reCalculateColumnCount();
  });

  onDestroy(() => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
  });

  function reCalculateColumnCountDebounce() {
    if (!window || !window.requestAnimationFrame) {
      // IE10+
      reCalculateColumnCount();
      return;
    }

    if (window.cancelAnimationFrame) {
      // IE10+
      window.cancelAnimationFrame(_lastRecalculateAnimationFrame);
    }

    _lastRecalculateAnimationFrame = window.requestAnimationFrame(() => {
      reCalculateColumnCount();
    });
  }

  function reCalculateColumnCount() {
    const windowWidth = masonryContainer.getClientRects()[0].width;
    let breakpointColsObject = breakpointCols;

    // Allow passing a single number to `breakpointCols` instead of an object
    if (typeof breakpointColsObject !== 'object') {
      breakpointColsObject = {
        default: breakpointColsObject || DEFAULT_COLUMNS,
      };
    }

    let matchedBreakpoint = Infinity;
    let columns = breakpointColsObject.default || DEFAULT_COLUMNS;

    for (let breakpoint in breakpointColsObject) {
      const optBreakpoint = parseInt(breakpoint);
      const isCurrentBreakpoint =
        optBreakpoint > 0 && windowWidth <= optBreakpoint;

      if (isCurrentBreakpoint && optBreakpoint < matchedBreakpoint) {
        matchedBreakpoint = optBreakpoint;
        columns = breakpointColsObject[breakpoint];
      }
    }

    columns = Math.max(1, columns || 1);

    if (_columnCount !== columns) {
      _columnCount = columns;
    }
  }

  function getShortestColumnIndex(columnWordLengths: number[]) {
    let index = 0;
    for (let i = 1; i < columnWordLengths.length; i++) {
      if (columnWordLengths[i] < columnWordLengths[index]) {
        index = i;
      }
    }

    return index;
  }

  function renderColumns(columnCount: number, items: BusinessReviewEntry[]) {
    const childrenInColumns = itemsInColumns(columnCount, items);
    let className = columnClassName;

    const columnAttributes = {
      className,
    };

    return childrenInColumns.map((items, i) => ({
      ...columnAttributes,
      items,
    }));
  }

  function itemsInColumns(columnCount: number, items: BusinessReviewEntry[]) {
    const DEVIATION = 500; // magic number to account for top and bottom spacing
    const currentColumnCount = columnCount;
    const itemsInColumns = new Array<BusinessReviewEntry[]>(currentColumnCount);

    const columnWordLengths: number[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const columnIndex =
        i / currentColumnCount < 1
          ? i % currentColumnCount
          : getShortestColumnIndex(columnWordLengths);

      const itemPointContribution =
        item.type === BusinessReviewEntryType.RAVELY_VIDEO
          ? 400
          : item.message.length;

      columnWordLengths[columnIndex] =
        (columnWordLengths[columnIndex] || 0) +
        DEVIATION +
        itemPointContribution;

      if (!itemsInColumns[columnIndex]) {
        itemsInColumns[columnIndex] = [];
      }

      itemsInColumns[columnIndex].push(item);
    }

    itemsInColumns.fill([], items.length);

    return itemsInColumns;
  }

  $: renderedColumns = renderColumns(_columnCount, reviews);
</script>

<div
  role="list"
  class="masonry {$$restProps.class || ''}"
  bind:this={masonryContainer}
>
  {#each renderedColumns as column}
    <div role="group" class={column.className || ''}>
      {#each column.items as item (item.id)}
        <div
          role="listitem"
          class="masonry-column"
          in:receive={{ key: item.id }}
          out:send={{ key: item.id }}
          animate:flip={{ duration: 100 }}
        >
          <SolidReviewCard review={item} {businessKey} {isCelebrity} />
        </div>
      {/each}
    </div>
  {/each}
</div>

<style lang="scss">
  .masonry {
    display: flex;
    justify-content: center;
    gap: 10px;
  }

  :global {
    .masonry-column {
      width: 350px;
      &:not(:last-child) {
        margin-bottom: 10px;
      }
    }
  }
</style>
